import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatMoney, formatPercent } from "@/lib/prefs";
import { computeRecipeCost, UNIT_KINDS } from "@/lib/units";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Utensils, GlassWater, Info, FileDown, GripVertical, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { AllergenBadges } from "@/components/AllergenBadges";
import { aggregateRecipeAllergens } from "@/lib/allergens";
import { printRecipe } from "@/lib/recipePdf";
import ImageUpload, { resolveImageUrl } from "@/components/ImageUpload";
import { Popover as SharePop, PopoverTrigger as ShareTrig, PopoverContent as ShareCont } from "@/components/ui/popover";
import SocialShare from "@/components/SocialShare";
import { Share2, QrCode, Link2, Download, Check } from "lucide-react";
import SellDialog from "@/components/SellDialog";
import WasteDialog from "@/components/WasteDialog";
import { QRCodeCanvas } from "qrcode.react";
import ReactMarkdown from "react-markdown";
import OnboardingTour from "@/components/OnboardingTour";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import LogPrepBatchDialog from "@/components/LogPrepBatchDialog";
import { applyBatchOverrides } from "@/lib/batchOverlay";
import { Scale, TriangleAlert } from "lucide-react";

function ShareRecipe({ recipe, allergens }) {
  const hashtags = ["culinaire", "chef", recipe.bracket, ...(allergens || []).slice(0, 3)];
  const title = `${recipe.name}: recipe from CulinaireSpecialist`;
  const publicPath = `/r/${recipe.id}`;
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}${publicPath}` : publicPath;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Public link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const downloadQr = () => {
    const canvas = document.querySelector('canvas[data-testid="recipe-share-qr-canvas"]');
    if (!canvas) { toast.error("QR not ready yet"); return; }
    const pngUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = `${(recipe.name || "recipe").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <SharePop>
      <ShareTrig asChild>
        <Button variant="outline" className="gap-2" data-testid="recipe-share-btn">
          <Share2 className="w-4 h-4" /> Share
        </Button>
      </ShareTrig>
      <ShareCont className="w-96" data-testid="recipe-share-popover">
        <div className="label-eyebrow mb-3 flex items-center gap-2"><QrCode className="w-3 h-3" /> Public recipe link</div>
        <div className="rounded-xl border border-border bg-secondary/40 p-3 flex gap-3 items-center">
          <div className="rounded-lg bg-white p-2 shrink-0">
            <QRCodeCanvas
              value={publicUrl}
              size={112}
              level="M"
              includeMargin={false}
              data-testid="recipe-share-qr-canvas"
            />
          </div>
          <div className="min-w-0 flex-1">
            <a
              href={publicPath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline break-all block"
              data-testid="recipe-share-link"
            >
              {publicUrl}
            </a>
            <div className="mt-2 flex flex-wrap gap-1">
              <Button size="sm" variant="outline" onClick={copy} className="h-7 gap-1 text-xs" data-testid="recipe-share-copy">
                {copied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadQr} className="h-7 gap-1 text-xs" data-testid="recipe-share-qr-download">
                <Download className="w-3 h-3" /> PNG
              </Button>
            </div>
          </div>
        </div>
        <div className="label-eyebrow mt-4 mb-2">Or post to social</div>
        <SocialShare url={publicUrl} title={title} hashtags={hashtags} testId="recipe-share" />
        <div className="text-[0.65rem] text-muted-foreground mt-3 leading-relaxed">
          The public page hides cost, margin, and sell price. Use it for menu cards and table QR codes.
        </div>
      </ShareCont>
    </SharePop>
  );
}

const ALL_UNITS = [...UNIT_KINDS.weight, ...UNIT_KINDS.volume, ...UNIT_KINDS.count];

// Prep duration conversion helpers (all stored as minutes internally).
const DURATION_UNITS = [
  { key: "min", label: "min", toMin: 1 },
  { key: "hr", label: "hr", toMin: 60 },
  { key: "day", label: "day", toMin: 60 * 24 },
  { key: "week", label: "week", toMin: 60 * 24 * 7 },
  { key: "month", label: "month", toMin: 60 * 24 * 30 }, // 30-day month
];

function bestDurationUnit(minutes) {
  if (minutes == null || minutes === "") return "min";
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return "min";
  if (m % DURATION_UNITS[4].toMin === 0) return "month";
  if (m % DURATION_UNITS[3].toMin === 0) return "week";
  if (m % DURATION_UNITS[2].toMin === 0) return "day";
  if (m % DURATION_UNITS[1].toMin === 0) return "hr";
  return "min";
}

function durationToUnitValue(minutes, unitKey) {
  const u = DURATION_UNITS.find((x) => x.key === unitKey) || DURATION_UNITS[0];
  const m = Number(minutes);
  if (!Number.isFinite(m)) return "";
  const v = m / u.toMin;
  // Trim trailing zeros nicely
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
}

function PrepTimeInput({ minutes, onChange }) {
  const [unit, setUnit] = useState(() => bestDurationUnit(minutes));
  const [text, setText] = useState(() => (minutes == null || minutes === "" ? "" : durationToUnitValue(minutes, bestDurationUnit(minutes))));

  useEffect(() => {
    // Keep local text/unit in sync when the recipe reloads
    const u = bestDurationUnit(minutes);
    setUnit(u);
    setText(minutes == null || minutes === "" ? "" : durationToUnitValue(minutes, u));
  }, [minutes]);

  const commit = (rawValue, rawUnit) => {
    if (rawValue === "" || rawValue == null) { onChange(null); return; }
    const v = Number(rawValue);
    if (!Number.isFinite(v)) { onChange(null); return; }
    const u = DURATION_UNITS.find((x) => x.key === rawUnit) || DURATION_UNITS[0];
    onChange(Math.round(v * u.toMin));
  };

  return (
    <div className="flex gap-1" data-testid="recipe-prep-time-wrap">
      <Input
        type="number"
        step="0.5"
        min="0"
        value={text}
        placeholder="auto"
        onChange={(e) => { setText(e.target.value); commit(e.target.value, unit); }}
        className="flex-1"
        data-testid="recipe-prep-time"
      />
      <Select value={unit} onValueChange={(v) => { setUnit(v); commit(text, v); }}>
        <SelectTrigger className="w-24" data-testid="recipe-prep-time-unit"><SelectValue /></SelectTrigger>
        <SelectContent>
          {DURATION_UNITS.map((u) => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipe, setRecipe] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [activeBatches, setActiveBatches] = useState({});
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [methodDraft, setMethodDraft] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [rowDraggable, setRowDraggable] = useState(null); // idx of row currently in "drag ready" mode
  const [platingTips, setPlatingTips] = useState("");
  const [loadingTips, setLoadingTips] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-open the onboarding tour when the user just carried a dish over from
  // the Landing simulator (URL: ?tour=1 or localStorage.cs_just_carried_dish_id
  // matches this recipe id). Runs once, then clears the flag/param.
  useEffect(() => {
    if (loading || !recipe) return;
    const wantByUrl = searchParams.get("tour") === "1";
    let wantByFlag = false;
    try {
      wantByFlag = localStorage.getItem("cs_just_carried_dish_id") === recipe.id;
    } catch {}
    if (wantByUrl || wantByFlag) {
      // Give the page a beat to lay out before spotlighting the first target.
      const t = setTimeout(() => setTourOpen(true), 300);
      return () => clearTimeout(t);
    }
  }, [loading, recipe, searchParams]);

  const closeTour = (completed) => {
    setTourOpen(false);
    try { localStorage.removeItem("cs_just_carried_dish_id"); } catch {}
    // Strip ?tour=1 so a refresh doesn't relaunch the tour.
    if (searchParams.get("tour")) {
      const next = new URLSearchParams(searchParams);
      next.delete("tour");
      setSearchParams(next, { replace: true });
    }
  };

  const fetchPlatingTips = async () => {
    if (!recipe?.image_url) {
      toast.error("Add a hero image first to get AI plating tips.");
      return;
    }
    setLoadingTips(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.id}/plating-tips`);
      setPlatingTips(data.tips || "");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Plating Coach is a Pro feature.", {
          action: { label: "Upgrade", onClick: () => navigate("/billing") },
        });
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    } finally {
      setLoadingTips(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, i, b] = await Promise.all([
        api.get(`/recipes/${id}`),
        api.get("/ingredients"),
        api.get("/batch-yield/active").catch(() => ({ data: { by_ingredient_id: {} } })),
      ]);
      setRecipe(r.data);
      setIngredients(i.data);
      setActiveBatches(b.data?.by_ingredient_id || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      navigate("/recipes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const byId = useMemo(
    () => applyBatchOverrides(Object.fromEntries(ingredients.map((i) => [i.id, i])), activeBatches),
    [ingredients, activeBatches],
  );

  // Which ingredients on this recipe currently have an active batch
  // override in effect. Surfaced as a Live batch banner so the chef
  // knows the food-cost math is running on real yield, not baseline.
  const activeBatchLines = useMemo(() => {
    if (!recipe || !recipe.ingredients) return [];
    return recipe.ingredients
      .map((line) => {
        const batch = activeBatches[line.ingredient_id];
        if (!batch) return null;
        const ing = byId[line.ingredient_id];
        return { batch, name: ing?.name || batch.ingredient_name };
      })
      .filter(Boolean);
  }, [recipe, activeBatches, byId]);

  // Debounce the recipe object by 300ms before feeding it into the cost
  // engine. Typing a price or quantity now updates the input instantly
  // while the food-cost math waits until the user has stopped typing.
  // Result: no jank, no CPU stall, and the "final" number still lands
  // less than a third of a second later.
  const [debouncedRecipe] = useDebouncedValue(recipe, 300);

  const costed = useMemo(
    () => (debouncedRecipe ? computeRecipeCost({ recipe: debouncedRecipe, ingredientsById: byId }) : null),
    [debouncedRecipe, byId]
  );

  const allergens = useMemo(
    () => (recipe ? aggregateRecipeAllergens(recipe, byId) : []),
    [recipe, byId]
  );

  const setField = (k, v) => { setRecipe((r) => ({ ...r, [k]: v })); setDirty(true); };

  const addLine = () => {
    if (!ingredients.length) return toast.error("Add ingredients to your pantry first.");
    setRecipe((r) => ({
      ...r,
      ingredients: [...r.ingredients, { ingredient_id: "", quantity: 1, unit: "g" }],
    }));
    setDirty(true);
  };

  const updateLine = (idx, patch) => {
    setRecipe((r) => {
      const next = [...r.ingredients];
      const merged = { ...next[idx], ...patch };
      // When user picks an ingredient, snap the unit to the ingredient's purchase unit
      if (patch.ingredient_id && patch.ingredient_id !== next[idx].ingredient_id) {
        const ing = ingredients.find((i) => i.id === patch.ingredient_id);
        if (ing) merged.unit = ing.purchase_unit_kind === "count" ? "each" : ing.purchase_unit;
      }
      next[idx] = merged;
      return { ...r, ingredients: next };
    });
    setDirty(true);
  };

  const removeLine = (idx) => {
    setRecipe((r) => ({ ...r, ingredients: r.ingredients.filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const reorderLine = (from, to) => {
    if (from === to || from == null || to == null) return;
    setRecipe((r) => {
      const next = [...r.ingredients];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...r, ingredients: next };
    });
    setDirty(true);
  };

  const addMethodStep = () => {
    if (!methodDraft.trim()) return;
    setRecipe((r) => ({ ...r, method: [...(r.method || []), methodDraft.trim()] }));
    setMethodDraft("");
    setDirty(true);
  };

  const removeMethodStep = (idx) => {
    setRecipe((r) => ({ ...r, method: r.method.filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: recipe.name,
        bracket: recipe.bracket,
        image_url: recipe.image_url,
        description: recipe.description,
        servings: Number(recipe.servings) || 1,
        sell_price: Number(recipe.sell_price) || 0,
        method: recipe.method,
        prep_time_minutes: recipe.prep_time_minutes,
        labor_rate_per_hour: recipe.labor_rate_per_hour,
        ingredients: recipe.ingredients
          .filter((l) => l.ingredient_id && Number(l.quantity) > 0)
          .map((l) => ({
            ingredient_id: l.ingredient_id,
            quantity: Number(l.quantity),
            unit: l.unit,
          })),
      };
      const { data } = await api.patch(`/recipes/${id}`, payload);
      setRecipe(data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this recipe?")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Deleted");
    navigate("/recipes");
  };

  if (loading || !recipe || !costed) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-testid="recipe-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/recipes" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-recipes">
          <ArrowLeft className="w-4 h-4" /> All recipes
        </Link>
        <div className="flex flex-wrap gap-2">
          <SellDialog recipe={recipe} />
          <WasteDialog recipe={recipe} />
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => printRecipe({ recipe, costed, ingredientsById: byId, allergens })}
            data-testid="recipe-export-pdf"
          >
            <FileDown className="w-4 h-4" /> Export PDF
          </Button>
          <ShareRecipe recipe={recipe} allergens={allergens} />
          <Button variant="outline" onClick={remove} className="text-destructive hover:text-destructive" data-testid="recipe-delete">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button onClick={save} disabled={!dirty || saving} className="gap-2" data-testid="recipe-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      {allergens.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="label-eyebrow">Allergens</div>
          <AllergenBadges allergens={allergens} testId="recipe-detail-allergens" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl overflow-hidden glass-card">
          {recipe.image_url ? (
            <img src={resolveImageUrl(recipe.image_url)} alt={recipe.name} className="w-full h-72 md:h-96 object-cover" />
          ) : (
            <div className="w-full h-72 md:h-96 bg-secondary flex items-center justify-center text-muted-foreground/40">
              {recipe.bracket === "drink" ? <GlassWater className="w-14 h-14" /> : <Utensils className="w-14 h-14" />}
            </div>
          )}
          {recipe.image_url && (
            <div className="px-6 pt-4 pb-2 flex items-center justify-between gap-3 border-b border-border">
              <div className="min-w-0">
                <div className="label-eyebrow text-[0.55rem] flex items-center gap-1.5"><Sparkles className="w-3 h-3" style={{ color: "#f2dfb5" }} /> Plating coach</div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">AI critique on plating, garnish and colour balance.</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPlatingTips}
                disabled={loadingTips}
                data-testid="plating-tips-btn"
                className="gap-1.5 shrink-0"
              >
                {loadingTips ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {platingTips ? "Regenerate" : "Get tips"}
              </Button>
            </div>
          )}
          {platingTips && (
            <div
              className="px-6 py-4 border-b border-border text-sm leading-relaxed"
              style={{
                background: "linear-gradient(180deg, rgba(232,207,154,0.08), rgba(232,207,154,0.02))",
              }}
              data-testid="plating-tips-body"
            >
              <div className="prose prose-sm prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-[color:rgb(242_223_181)] prose-strong:font-semibold">
                <ReactMarkdown>{platingTips}</ReactMarkdown>
              </div>
            </div>
          )}
          <div className="p-6 space-y-4">
            <div>
              <div className="label-eyebrow mb-1 capitalize">{recipe.bracket}</div>
              <Input
                value={recipe.name}
                onChange={(e) => setField("name", e.target.value)}
                className="font-display text-3xl h-auto py-2 border-transparent focus-visible:border-input px-0 focus-visible:px-3"
                data-testid="recipe-name-input"
              />
            </div>
            <ImageUpload
              value={recipe.image_url || ""}
              onChange={(v) => setField("image_url", v)}
              testId="recipe-image-upload"
              label="Hero image"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <div className="label-eyebrow text-[0.55rem] mb-1">Bracket</div>
                <Select value={recipe.bracket} onValueChange={(v) => setField("bracket", v)}>
                  <SelectTrigger data-testid="recipe-bracket"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="food">Food</SelectItem>
                    <SelectItem value="drink">Drink</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-eyebrow text-[0.55rem] mb-1">Servings</div>
                <Input type="number" min="1" value={recipe.servings} onChange={(e) => setField("servings", e.target.value)} data-testid="recipe-servings" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <div className="label-eyebrow text-[0.55rem] mb-1">Sell price</div>
                <Input type="number" step="0.01" value={recipe.sell_price} onChange={(e) => setField("sell_price", e.target.value)} data-testid="recipe-sell" />
              </div>
              <div>
                <div className="label-eyebrow text-[0.55rem] mb-1">Prep time</div>
                <PrepTimeInput
                  minutes={recipe.prep_time_minutes}
                  onChange={(mins) => setField("prep_time_minutes", mins)}
                />
              </div>
              <div className="col-span-2 sm:col-span-2">
                <div className="label-eyebrow text-[0.55rem] mb-1">Labor rate ($/hr)</div>
                <Input type="number" step="0.5" min="0" value={recipe.labor_rate_per_hour ?? ""} onChange={(e) => setField("labor_rate_per_hour", e.target.value === "" ? null : Number(e.target.value))} placeholder="auto" data-testid="recipe-labor-rate" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {activeBatchLines.length > 0 && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="active-batch-banner">
              <Scale className="w-4 h-4 mt-0.5 text-amber-200 shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="text-sm font-medium text-amber-100">
                  Live batch cost active on {activeBatchLines.length} ingredient{activeBatchLines.length === 1 ? "" : "s"}
                </div>
                <ul className="text-xs text-amber-100/80 space-y-1 tabular">
                  {activeBatchLines.map(({ batch, name }) => (
                    <li key={batch.id} className={batch.is_yield_alert ? "text-rose-200" : ""}>
                      {batch.is_yield_alert ? <TriangleAlert className="inline w-3 h-3 mr-1" /> : null}
                      {name}: {batch.actual_yield_percent}% actual, baseline {batch.baseline_yield_percent}%, expires {batch.expires_at ? new Date(batch.expires_at).toLocaleDateString() : ""}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl glass-card p-5">
              <div className="label-eyebrow mb-2">Cost / serving</div>
              <div className="font-display text-3xl tabular" data-testid="cost-per-serving">{formatMoney(costed.perServing)}</div>
              <div className="text-xs text-muted-foreground mt-1">Food {formatMoney(costed.foodCost / (recipe.servings || 1))} + Labor {formatMoney(costed.laborCost / (recipe.servings || 1))}</div>
            </div>
            <div className="rounded-2xl glass-card p-5">
              <div className="label-eyebrow mb-2">Food cost %</div>
              <div
                className={`font-display text-3xl tabular ${
                  costed.sellPrice > 0
                    ? costed.foodCostPercent > 35 ? "text-destructive" : "text-[hsl(var(--success))]"
                    : "text-muted-foreground"
                }`}
                data-testid="food-cost-percent"
              >
                {costed.sellPrice > 0 ? formatPercent(costed.foodCostPercent, 1) : "-"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Target &lt; 35%</div>
            </div>
            <div className="rounded-2xl glass-card p-5">
              <div className="label-eyebrow mb-2">Margin / serving</div>
              <div className="font-display text-3xl tabular" data-testid="margin-per-serving">{formatMoney(costed.margin)}</div>
              <div className="text-xs text-muted-foreground mt-1">Sell {formatMoney(costed.sellPrice)}</div>
            </div>
            <div className="rounded-2xl glass-card p-5">
              <div className="label-eyebrow mb-2">Batch total</div>
              <div className="font-display text-3xl tabular" data-testid="batch-total">{formatMoney(costed.total)}</div>
              <div className="text-xs text-muted-foreground mt-1">{recipe.servings} servings</div>
            </div>
          </div>

          <div className="rounded-2xl glass-card overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between">
              <h3 className="font-display text-xl">Ingredients</h3>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBatchDialogOpen(true)}
                  className="gap-1"
                  data-testid="recipe-log-batch-btn"
                >
                  <Scale className="w-4 h-4" /> Log batch
                </Button>
                <Button size="sm" variant="ghost" onClick={addLine} className="gap-1" data-testid="add-ingredient-btn">
                  <Plus className="w-4 h-4" /> Add
                </Button>
              </div>
            </div>
            <div className="divide-y divide-border/60">
              {recipe.ingredients.map((line, idx) => {
                const ing = byId[line.ingredient_id];
                const lineCost = costed.lines[idx]?.cost;
                const validUnits = ing
                  ? ing.purchase_unit_kind === "count"
                    ? UNIT_KINDS.count
                    : [...UNIT_KINDS[ing.purchase_unit_kind], ...(ing.density_g_per_ml ? UNIT_KINDS[ing.purchase_unit_kind === "weight" ? "volume" : "weight"] : [])]
                  : ALL_UNITS;
                return (
                  <div
                    key={idx}
                    className={[
                      "px-4 py-3 space-y-2 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center transition-colors",
                      dragOverIdx === idx && dragIdx !== null && dragIdx !== idx ? "bg-primary/10" : "",
                      dragIdx === idx ? "opacity-40" : "",
                    ].join(" ")}
                    data-testid={`line-${idx}`}
                    draggable={rowDraggable === idx}
                    onDragStart={(e) => {
                      setDragIdx(idx);
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (dragOverIdx !== idx) setDragOverIdx(idx);
                    }}
                    onDragLeave={() => setDragOverIdx((cur) => (cur === idx ? null : cur))}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIdx ?? Number(e.dataTransfer.getData("text/plain"));
                      reorderLine(from, idx);
                      setDragIdx(null);
                      setDragOverIdx(null);
                      setRowDraggable(null);
                    }}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); setRowDraggable(null); }}
                  >
                    <div className="sm:col-span-5 flex items-center gap-2">
                      <span
                        className="text-muted-foreground/60 hover:text-foreground cursor-grab active:cursor-grabbing select-none shrink-0"
                        title="Drag to reorder"
                        onMouseDown={() => setRowDraggable(idx)}
                        onMouseUp={() => setRowDraggable(null)}
                        onTouchStart={() => setRowDraggable(idx)}
                        onTouchEnd={() => setRowDraggable(null)}
                        data-testid={`line-${idx}-drag`}
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <Select
                          value={line.ingredient_id || undefined}
                          onValueChange={(v) => updateLine(idx, { ingredient_id: v })}
                        >
                          <SelectTrigger data-testid={`line-${idx}-ing`}>
                            <SelectValue placeholder="Select ingredient…" />
                          </SelectTrigger>
                          <SelectContent>
                            {ingredients.map((i) => (
                              <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:contents">
                      <Input
                        type="number" step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                        className="flex-1 sm:col-span-2 sm:flex-none"
                        data-testid={`line-${idx}-qty`}
                      />
                      <div className="flex-1 sm:col-span-2 sm:flex-none">
                        <Select value={line.unit} onValueChange={(v) => updateLine(idx, { unit: v })}>
                          <SelectTrigger data-testid={`line-${idx}-unit`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(validUnits || ALL_UNITS).map((u) => (
                              <SelectItem key={u} value={u}>{u}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2 text-right tabular font-medium flex items-center justify-end gap-1 whitespace-nowrap">
                        {!line.ingredient_id ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : lineCost?.error ? (
                          <span className="text-xs text-destructive">{lineCost.error}</span>
                        ) : (
                          <>
                            {formatMoney((lineCost?.total || 0))}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="text-muted-foreground hover:text-foreground" data-testid={`line-${idx}-info`}>
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 text-xs">
                                <div className="label-eyebrow mb-2">Conversion math</div>
                                <ul className="space-y-1 tabular">
                                  {(lineCost?.steps || []).map((s, i) => (
                                    <li key={i} className="text-muted-foreground">{s}</li>
                                  ))}
                                </ul>
                              </PopoverContent>
                            </Popover>
                          </>
                        )}
                      </div>
                      <div className="sm:col-span-1 sm:text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`line-${idx}-remove`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!recipe.ingredients.length && (
                <div className="px-5 py-8 text-center text-muted-foreground text-sm">Add ingredients to compute cost.</div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-border bg-secondary/40 flex justify-between text-sm">
              <span className="label-eyebrow">Batch total</span>
              <span className="font-display text-lg tabular">{formatMoney(costed.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Method */}
      <div className="rounded-2xl glass-card overflow-hidden">
        <div className="px-5 py-4">
          <h3 className="font-display text-xl">Method</h3>
        </div>
        <ol className="divide-y divide-border/60">
          {(recipe.method || []).map((step, idx) => (
            <li key={idx} className="px-5 py-3 flex gap-3 items-start" data-testid={`method-${idx}`}>
              <span className="w-6 h-6 shrink-0 rounded-full bg-primary/15 text-primary font-semibold text-xs flex items-center justify-center tabular">
                {idx + 1}
              </span>
              <span className="flex-1 text-sm leading-relaxed">{step}</span>
              <Button size="sm" variant="ghost" onClick={() => removeMethodStep(idx)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ol>
        <div className="px-5 py-3 border-t border-border bg-secondary/30 flex gap-2">
          <Input
            placeholder="Add a method step…"
            value={methodDraft}
            onChange={(e) => setMethodDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMethodStep()}
            data-testid="method-draft-input"
          />
          <Button onClick={addMethodStep} variant="secondary" data-testid="method-add-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {tourOpen && (
        <OnboardingTour
          steps={[
            {
              selector: '[data-testid="recipe-name-input"]',
              title: `Welcome: meet ${recipe?.name || "your dish"}.`,
              body: "This recipe was carried over from the landing demo. The next four steps will audit its cost from scratch.",
              placement: "right",
            },
            {
              selector: '[data-testid="add-ingredient-btn"]',
              title: "Add ingredients: start here.",
              body: "Tap Add to pull items from your pantry, set the quantity and unit, and the cost lands live.",
              placement: "left",
            },
            {
              selector: '[data-testid="cost-per-serving"]',
              title: "Cost lands live.",
              body: "Every gram, drop, and yield is reconciled here. Food, labour, and margin recompute on every keystroke.",
              placement: "bottom",
            },
            {
              selector: '[data-testid="recipe-sell"]',
              title: "Set your sell price.",
              body: "Enter the sell price. Green food cost percent means the plate is under the 35 percent guardrail. Red means the plate is running above it.",
              placement: "top",
            },
            {
              selector: '[data-testid="recipe-save"]',
              title: "Save and go build your menu.",
              body: "Hit Save when the plate is ready. Recipes, pantry, roster, and sales stay linked. Cost once. Audit every service.",
              placement: "bottom",
            },
          ]}
          onFinish={closeTour}
        />
      )}

      <LogPrepBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        ingredients={ingredients}
        defaultIngredientId={
          recipe?.ingredients?.find((l) => {
            const ing = ingredients.find((x) => x.id === l.ingredient_id);
            const kind = (ing?.purchase_unit_kind || "").toLowerCase();
            return kind === "mass" || kind === "weight";
          })?.ingredient_id
        }
        onLogged={() => { load(); }}
      />
    </div>
  );
}
